import { isStrongPassword } from '@dadohr/shared/domainTypes/password';
import { css } from '@emotion/css';
import { TextField, Typography } from '@material-ui/core';
import { useState } from 'react';
import ReactHelmet from 'react-helmet';
import { ButtonLoader } from '../../../../components/ButtonWithPromise';
import { Form } from '../../../../components/Form';
import { FormField } from '../../../../components/Form/FormField';
import { FormSubmitButton } from '../../../../components/Form/FormSubmitButton';
import { wait } from '../../../../helpers';
import { useSnackbar } from '../../../../hooks/useSnackbar';
import { useUnsafeTranslation } from '../../../../i18next';
import { FlexContainer } from '../../../../layout/Flex';
import { TopNavBarLogoOnly } from '../../../../layout/TopNav';
import { useRoutes, useStringQueryParam } from '../../../../routes';
import { signInWithEmailAndPassword } from '../../../../services/auth';
import { LightMdToJsx } from '../../../../services/formatter';
import { WEAK_PASSWORD_SNACK } from '../../../../services/password';
import { completeAdminRegistration } from '../../services';
import { SignUpContainer, SignUpWrapper } from '../Overview';

type ConfirmationValues = {
  password: string;
  confirmPassword: string;
  workEmail: string;
};

const AccountConfirmation = ({
  initialValues,
  confirmPasswordError,
  onSubmit
}: {
  initialValues: ConfirmationValues;
  confirmPasswordError: React.ReactNode;
  onSubmit: (values: ConfirmationValues) => Promise<void>;
}) => {
  const { t } = useUnsafeTranslation();

  return (
    <SignUpContainer>
      <Typography component="h3" variant="h3">
        {t('Please verify your email and set a password')}
      </Typography>
      <Form<ConfirmationValues>
        initialVales={initialValues}
        onSubmit={onSubmit}
        className={css({ width: '100%' })}
      >
        {({ submitting, values }) => (
          <FlexContainer
            fullWidth
            spacing={2}
            direction="column"
            alignItems="flex-start"
          >
            <Typography>
              {t('Input the same email address you used to sign up')}
            </Typography>
            <FormField name="workEmail">
              {({ input }) => (
                <TextField
                  {...input}
                  label={t('Work Email')}
                  variant="outlined"
                  fullWidth
                />
              )}
            </FormField>
            <FormField name="password">
              {({ input }) => (
                <TextField
                  {...input}
                  label={t('Password')}
                  variant="outlined"
                  type="password"
                  fullWidth
                />
              )}
            </FormField>
            <FormField name="confirmPassword">
              {({ input }) => (
                <TextField
                  {...input}
                  label={t('Confirm Password')}
                  variant="outlined"
                  fullWidth
                  type="password"
                  error={!!confirmPasswordError}
                  helperText={confirmPasswordError}
                />
              )}
            </FormField>
            <FormSubmitButton
              submitting={submitting}
              submitComponent={
                <>
                  <ButtonLoader /> {t('Verifying...')}
                </>
              }
              disabled={
                !values.confirmPassword || !values.password || !values.workEmail
              }
            >
              {t('Verify')}
            </FormSubmitButton>
          </FlexContainer>
        )}
      </Form>
    </SignUpContainer>
  );
};

export const ConfirmationOverview = () => {
  const { t } = useUnsafeTranslation();
  const { ROUTES, goTo } = useRoutes();
  const [token] = useStringQueryParam('t');
  const [
    confirmPasswordError,
    setConfirmPasswordError
  ] = useState<React.ReactNode>('');
  const { enqueueSnackbarError } = useSnackbar();

  const initialValues: ConfirmationValues = {
    password: '',
    confirmPassword: '',
    workEmail: ''
  };

  const onSubmit = async (values: ConfirmationValues) => {
    if (values.password !== values.confirmPassword) {
      setConfirmPasswordError(t('Does not match your password'));
      return;
    }
    if (!isStrongPassword(values.password)) {
      setConfirmPasswordError(
        <div style={{ whiteSpace: 'pre-line' }}>
          {WEAK_PASSWORD_SNACK.message(t)}
        </div>
      );
      return;
    }
    const confirmValidationResult = await completeAdminRegistration({
      email: values.workEmail,
      password: values.password,
      token: token
    });

    if (confirmValidationResult.error) {
      if (confirmValidationResult.error === 'WEAK_PASSWORD') {
        setConfirmPasswordError(
          <div style={{ whiteSpace: 'pre-line' }}>
            {WEAK_PASSWORD_SNACK.message(t)}
          </div>
        );
      }
      if (confirmValidationResult.error === 'INVALID_TOKEN') {
        enqueueSnackbarError(
          <LightMdToJsx>
            {t(
              'Something went wrong please contact **{{help}}** for more information.',
              {
                help: 'help@dadohr.com'
              }
            )}
          </LightMdToJsx>
        );
      }
      if (confirmValidationResult.error === 'ADMIN_EMAIL_INVALID') {
        enqueueSnackbarError(
          <LightMdToJsx>
            {t(
              'Email address is not valid, contact **{{help}}** if you need help.',
              {
                help: 'help@dadohr.com'
              }
            )}
          </LightMdToJsx>
        );
      }
    } else {
      await signInWithEmailAndPassword(values.workEmail, values.password).then(
        async () => {
          // signInWithEmailAndPassword does authenticate the user - but the hook
          // to deal with this has some other requests to make.
          // Moving directly to this route therefore can cause a brief flash
          // of seeing the actual login screen.
          // The right moment in time to move over is not entirely trivial to predict,
          // as this page is still outside of the regular frame of the page.
          // Let's try with a naive approach and just wait for a bit.
          // A better solution would be to register the success here, show some form
          // of "signing in" while we wait for the useLogin hook to complete.
          await wait(1500);
          goTo(ROUTES.signUp.updateOrgInfo.url());
        }
      );
    }
  };
  return (
    <SignUpWrapper>
      <ReactHelmet>
        <title>Dado Moments | Sign up</title>
      </ReactHelmet>
      <TopNavBarLogoOnly />
      <FlexContainer direction="column" marginTop={4} spacing={4}>
        <AccountConfirmation
          confirmPasswordError={confirmPasswordError}
          onSubmit={onSubmit}
          initialValues={initialValues}
        />
      </FlexContainer>
    </SignUpWrapper>
  );
};
